import React, { useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styles from './StickyActionBar.module.scss';
import {
  claimScrollPadding,
  findInnerScrollContainer,
  releaseScrollPadding,
} from './scrollPadding';

/* ────────────────────────────────────────────────────────────
 * StickyActionBar — bottom-pinned save/discard bar for long forms.
 *
 * Required on every form > 1 viewport of fields
 * (INVENTORY_UI_PLAN.md §6: AddItems, AddDiscount, StoreConfig,
 * AddNewStore, EditCategory).
 *
 * Default usage (driven by dirty-state from the hook):
 *
 *   <StickyActionBar
 *     visible={isDirty}
 *     message={hasErrors ? 'Fix errors before saving' : 'Unsaved changes'}
 *     left={<Button variant="ghost" onClick={onDiscard}>Discard</Button>}
 *     right={<Button onClick={onSave} loading={saving}>Save changes</Button>}
 *   />
 *
 * Motion (MOTION.md §4.2 / §4.10):
 *   - Enter: slide up from below + fade, --transition-modal.
 *   - Exit: slide down + fade, --transition-base.
 *   - Backdrop blur for the glass surface.
 *
 * Focus: while visible the bar reserves its own height (+16px) as
 * scroll-padding-bottom on the page, so a control reached with Tab is
 * scrolled to just ABOVE the bar instead of underneath it. See
 * ./scrollPadding.js for why and how several bars share it.
 * ──────────────────────────────────────────────────────────── */

const StickyActionBar = ({
  visible,
  message,
  left,
  right,
  variant,
  className,
}) => {
  const barRef = useRef(null);
  // A stable identity for this bar's claim in the scroll-padding
  // registry (any unique object works; the ref object is one).
  const claimToken = barRef;

  // useLayoutEffect so the padding is in place before the browser
  // paints — and so before any focus change that follows the bar
  // appearing. Hooks must run before the `visible` early return below.
  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!visible || !bar || typeof document === 'undefined') return undefined;

    const root = document.documentElement;
    const inner = findInnerScrollContainer(bar);
    const claim = () => {
      // offsetHeight ignores the enter animation's translateY, so the
      // first measurement is already the final height.
      const height = bar.offsetHeight;
      claimScrollPadding(root, claimToken, height);
      if (inner) claimScrollPadding(inner, claimToken, height);
    };
    claim();

    // The bar changes height when its content wraps (phone widths stack
    // message, discard and save into a column) or the message changes.
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(claim);
      ro.observe(bar);
    }

    return () => {
      if (ro) ro.disconnect();
      releaseScrollPadding(root, claimToken);
      if (inner) releaseScrollPadding(inner, claimToken);
    };
  }, [visible, claimToken]);

  if (!visible) return null;

  const classNames = [
    styles.bar,
    variant === 'danger' ? styles.barDanger : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={barRef} className={classNames} role="region" aria-label="Save changes">
      <div className={styles.inner}>
        <div className={styles.left}>
          {message && <span className={styles.message}>{message}</span>}
          {left}
        </div>
        <div className={styles.right}>{right}</div>
      </div>
    </div>
  );
};

StickyActionBar.propTypes = {
  visible: PropTypes.bool,
  message: PropTypes.node,
  left: PropTypes.node,
  right: PropTypes.node,
  variant: PropTypes.oneOf(['default', 'danger']),
  className: PropTypes.string,
};

StickyActionBar.defaultProps = {
  visible: false,
  message: '',
  left: null,
  right: null,
  variant: 'default',
  className: '',
};

export default StickyActionBar;
